import * as React from 'react';
import {
  useListInventoryLifecyclePreviewBookings,
  type InventoryLifecycleBookings, type InventoryLifecycleIdentity
} from '@cdf/api-client-react';
import { Button } from '@/components/ui/button';
import { formatLifecycleTime } from '@/lib/inventory-lifecycle-time';
import { getErrorMessage } from '@/lib/utils';

export function InventoryLifecycleSpaceBookings({ previewId, space, timezone, summary }: {
  previewId: string; space: InventoryLifecycleIdentity; timezone: string;
  summary?: { cancellationCount: number; underwayCount: number };
}) {
  const [expanded, setExpanded] = React.useState(false);
  const [offset, setOffset] = React.useState(0);
  const total = summary ? summary.cancellationCount + summary.underwayCount : undefined;
  const page = useListInventoryLifecyclePreviewBookings<InventoryLifecycleBookings, Error>(
    previewId, { spaceId: space.id, limit: 20, offset },
    { query: { enabled: expanded && total !== 0, retry: false } }
  );
  return <div className="grid content-start gap-2 text-sm" aria-label={'Booking impact for ' + space.name}>
    {summary ? <p>
      {summary.cancellationCount > 0 ? summary.cancellationCount + ' to cancel' : 'No bookings to cancel'}
      {summary.underwayCount > 0 ? ' · ' + summary.underwayCount + ' underway — may finish' : ''}
    </p> : null}
    {total !== 0 ? <Button type="button" variant="outline" size="sm" className="justify-self-start"
      aria-expanded={expanded} aria-label={(expanded ? 'Hide' : 'View') + ' bookings for ' + space.name}
      onClick={() => setExpanded(value => !value)}>
      {expanded ? 'Hide bookings' : 'View bookings'}{total !== undefined ? ' (' + total + ')' : ''}
    </Button> : null}
    {expanded ? <>
      {page.isFetching ? <p role="status">Loading bookings…</p> : null}
      {page.error ? <p role="alert" className="text-destructive">{getErrorMessage(page.error)}</p> : null}
      {page.data && !page.isFetching ? <>
        {page.data.total === 0 ? <p>No affected bookings.</p> : <ul className="grid gap-2">
          {page.data.items.map(booking => <li key={booking.id} className="border-l-2 pl-2">
            <p>{formatLifecycleTime(booking.start, timezone)} – {formatLifecycleTime(booking.end, timezone)}</p>
            <p className="text-muted-foreground">{booking.effect === 'underway_preserved' ? 'Underway — may finish' : 'Cancelled on confirmation'}</p>
          </li>)}
        </ul>}
        {page.data.total > 20 ? <div className="flex flex-wrap items-center gap-2 text-xs">
          <span>{offset + 1}–{Math.min(offset + 20, page.data.total)} of {page.data.total} bookings</span>
          <Button type="button" variant="outline" size="sm" disabled={offset === 0}
            aria-label={'Previous bookings for ' + space.name} onClick={() => setOffset(value => Math.max(0, value - 20))}>Previous</Button>
          <Button type="button" variant="outline" size="sm" disabled={offset + 20 >= page.data.total}
            aria-label={'Next bookings for ' + space.name} onClick={() => setOffset(value => value + 20)}>Next</Button>
        </div> : null}
      </> : null}
    </> : null}
  </div>;
}
